import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Elegant homepage ambient audio control.
 *  - Muted by default (mobile autoplay friendly — audio element starts muted)
 *  - Lazy fade-in only when user taps unmute
 *  - Remembers preference in sessionStorage so it carries across page navigation
 *    within the same browsing session, but resets when the tab closes
 *  - Mobile autoplay policy compliant: we never .play() without a user gesture
 *    unless the element is muted (Safari + Chrome allow muted autoplay)
 */
const STORAGE_KEY = "wls-ambient";
const TARGET_VOLUME = 0.32;
const FADE_MS = 1100;

export default function AmbientAudio() {
  const ref = useRef(null);
  const fadeTimer = useRef(null);
  const [muted, setMuted] = useState(true);
  const [ready, setReady] = useState(false);

  // Restore preference + start element muted so autoplay is permitted
  useEffect(() => {
    const audio = ref.current;
    if (!audio) return;
    audio.volume = 0;
    audio.muted = true;
    // Try to start playback (muted autoplay is allowed on all modern browsers).
    // If blocked, the play promise rejects silently — that's fine.
    const p = audio.play();
    if (p && typeof p.catch === "function") p.catch(() => {});

    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved === "on") {
      // Don't auto-unmute on first arrival — must wait for user gesture.
      // We just visually mark intent and unmute on the first interaction.
      const unmuteOnGesture = () => {
        toggle();
        document.removeEventListener("pointerdown", unmuteOnGesture);
      };
      document.addEventListener("pointerdown", unmuteOnGesture, { once: true });
    }
    setReady(true);
    return () => { if (fadeTimer.current) clearInterval(fadeTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fadeTo = (target) => {
    const audio = ref.current;
    if (!audio) return;
    if (fadeTimer.current) clearInterval(fadeTimer.current);
    const start = audio.volume;
    const startedAt = performance.now();
    fadeTimer.current = setInterval(() => {
      const t = Math.min(1, (performance.now() - startedAt) / FADE_MS);
      audio.volume = start + (target - start) * t;
      if (t >= 1) {
        clearInterval(fadeTimer.current);
        fadeTimer.current = null;
        if (target === 0) audio.muted = true;
      }
    }, 16);
  };

  const toggle = () => {
    const audio = ref.current;
    if (!audio) return;
    if (muted) {
      audio.muted = false;
      // Resume from any paused state (e.g. autoplay blocked)
      const p = audio.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
      fadeTo(TARGET_VOLUME);
      setMuted(false);
      sessionStorage.setItem(STORAGE_KEY, "on");
    } else {
      fadeTo(0);
      setMuted(true);
      sessionStorage.setItem(STORAGE_KEY, "off");
    }
  };

  return (
    <>
      <audio
        ref={ref}
        src="/audio/ambient.mp3"
        loop
        preload="metadata"
        playsInline
        // muted is controlled imperatively
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={muted ? "Play ambient music" : "Mute ambient music"}
        aria-pressed={!muted}
        data-testid="ambient-audio-toggle"
        className={`fixed bottom-5 left-5 z-40 w-11 h-11 rounded-full flex items-center justify-center border transition-all duration-500 ${
          muted
            ? "border-white/15 bg-black/55 text-white/65 hover:text-gold hover:border-gold"
            : "border-gold/50 bg-black/65 text-gold shadow-[0_0_24px_-6px_rgba(212,175,55,0.6)]"
        } backdrop-blur-md ${ready ? "opacity-100" : "opacity-0"}`}
      >
        {muted ? <VolumeX size={17} strokeWidth={1.6} /> : <Volume2 size={17} strokeWidth={1.6} />}
        {!muted && (
          <span className="absolute inset-0 rounded-full border border-gold/40 animate-ping pointer-events-none" />
        )}
      </button>
    </>
  );
}
